import { COLLISION_SKIN, type CollisionWorld } from './collision/world.js';
import {
  addScaled,
  clone,
  dot,
  length,
  normalize,
  projectOnPlane,
  scale,
  type Vec3,
  vec3,
} from './math.js';
import { BUTTON, TICK_RATE } from './protocol.js';

/**
 * The character controller — the one piece of gameplay code that MUST produce
 * identical results on the client and the server.
 *
 * Rules that make prediction agree with authority, all of them easy to break:
 *
 * 1. **Fixed timestep, always.** Integration uses `1 / TICK_RATE`, never the
 *    client's reported `dtMs`. That field exists for telemetry and anti-cheat
 *    (detecting implausible frame times); feeding it into integration would let a
 *    client claim a long frame and travel further, and would make the two sides
 *    disagree whenever a frame time differed.
 * 2. **Predict from the encoded input, not the raw one.** The client must encode
 *    its input, decode it back, and predict from the decoded values. Yaw is
 *    quantized to 16 bits on the wire, so predicting from the raw float diverges
 *    from what the server integrates — a small, constant, maddening drift.
 * 3. **No wall-clock, no randomness, no iteration over unordered collections.**
 *    Nothing in here may read the clock or a Map's insertion order.
 */

// ─── Tuning ─────────────────────────────────────────────────────────
// Deliberately un-physical: 20 m/s² gravity and 60 m/s² ground acceleration make
// movement feel responsive. Realism is not the target; Cryzen-class snappiness is.

export const MOVEMENT = {
  /** Capsule radius — a 0.8m-wide player. */
  radius: 0.4,
  /** Total standing height, including the capsule's end caps. */
  height: 1.8,
  eyeHeight: 1.6,
  /**
   * Capsule height while crouching or sliding.
   *
   * Used for *hit resolution and rendering*, not for collision: the movement capsule stays
   * full height, so crouching never opens a gap a standing player could not already fit
   * through. That asymmetry is safe in one direction only — a player can be shot anywhere
   * they can go — and it avoids the hardest part of a real crouch, which is refusing to let
   * someone stand up under a ceiling.
   *
   * One constant for both the drawn figure and the hit capsule, so the thing you aim at and
   * the thing you hit cannot drift apart. Sliding made this matter: before it, crouching was
   * a 3.2 m/s stance nobody used in the open; now players cross the map at 12.5 m/s in it.
   */
  crouchHeight: 1.35,

  gravity: 20,
  // Second cut of the 2026-08-19 playtest day: 7/9.5 read as sprinting/speed-hack, 5.4/7.4
  // still read as too fast, and 4.6/6.3 sat on CS's ~4.6 m/s run. Third cut, 2026-09-09
  // (game speed a touch slower): everything below is 9% slower than that, the
  // ratios untouched — sprint 1.36× walk, slide 1.32× sprint, air cap 1.1× sprint.
  walkSpeed: 4.2,
  sprintSpeed: 5.7,
  crouchSpeed: 2.1,
  /** Ground acceleration, in m/s². */
  groundAccel: 60,
  /** Air acceleration is much lower — you commit to a jump. */
  airAccel: 12,
  /** Friction coefficient applied while grounded. */
  friction: 8,
  jumpSpeed: 6.5,

  /** Obstacles at or below this height are walked over without jumping. */
  stepHeight: 0.35,
  /** Steepest walkable surface: 50° from horizontal. */
  minGroundNormalY: Math.cos((50 * Math.PI) / 180),
  /** How far below the feet to look for ground. */
  groundProbe: 0.12,
  /** Vertical speed above which ground snapping is skipped (i.e. rising). */
  maxSnapUpSpeed: 0.1,

  /** Iterations of collide-and-slide per move. 4 handles floor + two walls. */
  slideIterations: 4,

  // ─── Crouch-slide ──────────────────────────────────────────────────────────
  // The mechanic that makes speed a resource rather than a constant. Sprint, tap crouch, and
  // you trade the ability to steer for a burst you cannot get any other way — which is what
  // gives a corner a right and a wrong approach speed.
  /** Launch speed. Above `sprintSpeed`, or a slide would be a slower way to travel. */
  slideSpeed: 7.5,
  /** Friction while sliding. A fifth of normal — this number *is* the mechanic. */
  slideFriction: 1.6,
  /** Below this the slide ends; the player stands rather than creeping along. */
  slideMinSpeed: 3.4,
  /** Hard cap, so a slide down a slope cannot become a permanent state. */
  slideMaxSeconds: 0.9,
  /** Gap before another slide may start, so it cannot be chain-tapped for free speed. */
  slideCooldownSeconds: 0.55,
  /** Entry threshold: you must already be moving to convert speed into a slide. */
  slideEntrySpeed: 4.6,

  /**
   * Friction while pressing *against* the direction you are travelling, on the ground.
   *
   * Counter-strafing: to stop quickly you tap the opposite key rather than releasing. Nearly
   * four times normal friction, which takes a sprint from 9.5 m/s to under `crouchSpeed` in
   * about two ticks (~30ms) instead of about eight (~130ms).
   *
   * This is a *shooting* mechanic wearing movement's clothes, and it already has a consumer:
   * `spread.ts` restores first-shot accuracy at `speed <= crouchSpeed`, so the reward for
   * stopping properly is that your first bullet goes exactly where you aimed. Before this the
   * only way to earn that was to stop moving and wait, which in a firefight means dying
   * accurately. Now it is a technique with a timing to learn.
   */
  counterStrafeFriction: 30,

  /**
   * Ceiling on the horizontal speed air acceleration may build. 10% above `sprintSpeed`.
   *
   * The Quake accelerator only ever adds the shortfall *along the wish direction*, so speed
   * already carried elsewhere is untouched — which is what makes air control feel right, and
   * which also means that holding strafe while rotating yaw keeps `dot(velocity, wishDir)`
   * near zero and adds the full `airAccel × wishSpeed × dt` every airborne tick. At
   * `airAccel: 12` and `sprintSpeed: 7.4` that is up to 1.5 m/s per tick, compounding, and
   * nothing bounded it: friction is grounded-only, so re-jumping on the landing tick pays one
   * frame of it per hop and keeps the rest.
   *
   * That is bunnyhopping, and it is a ~20-line script rather than a hack — no protocol
   * violation, so nothing noticed, and the server simulated it faithfully because it runs the
   * same code. Skilled players would have found the manual version within days of launch.
   *
   * The reason it has to be a cap and not a detector: `slideSpeed` is the fastest *designed*
   * state in the game, and the slide is deliberately capped, cooled down and un-steerable so
   * that speed is a resource you spend. Air-strafing routed around all three. Nobody should
   * be flagged for a mechanic the server simulated for them, so this changes the movement
   * model for everyone identically instead.
   *
   * **A ceiling on what acceleration may add, not a clamp on how fast you may be.** The first
   * version clamped total speed after accelerating, which was wrong in a way that only shows
   * up in the good case: a slide launches at 12.5 and jumping out of it is *supposed* to
   * carry that, so a hard clamp at any value below the carry quietly deleted the reward for
   * setting up a slide. Now acceleration simply cannot push you past the cap, and anything
   * you are already carrying above it decays through friction and gravity the way it always
   * did. Nothing is ever reduced.
   *
   * That change is what let the number come down. The rule is **10% over `sprintSpeed`**, so
   * air-strafing is a small reward for jumping well rather than a movement mode, and
   * `slideSpeed` is once again strictly the fastest thing in the game — which was the design
   * all along. It was 14.4, which beat the slide, which made the slide's cooldown and loss of
   * steering a bad trade.
   *
   * **6.95 rather than 10.5, because the rule is a ratio and this branch cut the pace.** The
   * ceiling arrived here from `main` as `10.5`, correct there: 10% over a `sprintSpeed` of
   * 9.5. This branch's second pace cut (`a8fad87`) took walk to 4.6 and sprint to 6.3, and
   * 10.5 against a 6.3 sprint is 67% headroom — a cap that bounds nothing, which is worse than
   * no cap because it looks like a fix. 6.3 x 1.1 = 6.93, rounded to 6.95.
   *
   * This also retires the older figure this branch had (9.55 = `slideSpeed` x 1.15). That
   * number existed to leave the slide's carried momentum room *underneath a clamp on total
   * speed* — which is exactly the mistake the paragraph above describes. With a ceiling on
   * acceleration instead, nothing carried is ever reduced, so no headroom is needed and the
   * cap can sit where the design wants it: below the slide, just above a sprint.
   *
   * **This lives in `shared/` on purpose.** A cap the server applied and the client did not
   * would be a correction on every airborne tick — a predicted thing the server refuses to
   * do, which is the divergence-with-no-correction-path CLAUDE.md warns about.
   */
  airSpeedCap: 6.27,

  // ─── Vault ─────────────────────────────────────────────────────────────────
  // Waist-high cover is the commonest obstacle on the map, and jumping it is a gamble: the
  // jump apex is 1.06m against 0.9m crates, so it works and feels like luck. A vault makes
  // the same geometry a decision instead.
  /** Tallest ledge that can be vaulted. Above `stepHeight`, below a 1.8m sightline breaker. */
  vaultMaxHeight: 1.25,
  /** How far ahead of the capsule a ledge is looked for. */
  vaultReach: 0.75,
  /** Upward speed a vault gives — enough to clear `vaultMaxHeight` with margin. */
  vaultUpSpeed: 5.6,
  /** Forward speed carried through the vault, so it keeps momentum rather than stalling. */
  vaultForwardSpeed: 5.0,
} as const;

/** Half the cylindrical section of the capsule, excluding caps (Rapier's convention). */
export const CAPSULE_HALF_HEIGHT = MOVEMENT.height / 2 - MOVEMENT.radius;

export const FIXED_DT = 1 / TICK_RATE;

export interface MoveState {
  position: Vec3;
  velocity: Vec3;
  grounded: boolean;
  /**
   * Seconds left in the current slide, or 0 when not sliding.
   *
   * Kept in the movement state rather than on the wire, because it is *derivable*: the same
   * inputs through the same controller produce the same slide on both tiers, so sending it
   * would be paying 20 times a second to tell the client something it already computed. The
   * same reasoning as the grenade's arc — broadcast the cause, never the flight.
   */
  slideSecondsLeft: number;
  /** Seconds until another slide may start. Also derived; also never sent. */
  slideCooldownSeconds: number;
  /**
   * True on the tick a vault fired.
   *
   * Exists for presentation only — a client reads it to play a sound and kick the camera.
   * Nothing in the simulation branches on it, so a client that ignores it is not wrong, just
   * quieter.
   */
  vaulted: boolean;
}

export interface MovementInput {
  buttons: number;
  yaw: number;
  /**
   * Speed multiplier from the weapon in hand, including its ADS state.
   *
   * Passed in rather than read from a weapon here, because `movement.ts` must not depend on
   * the weapon roster — the controller is the one piece of code that has to stay identical
   * on both tiers, and giving it a reason to know about loadouts is how that stops being
   * true. The caller computes it (`weaponSpeedMultiplier`) and the controller just scales.
   *
   * Defaults to 1, so every existing caller and the golden movement trace are unaffected.
   */
  speedMultiplier?: number;
}

export function spawnMoveState(position: Vec3): MoveState {
  return {
    position: clone(position),
    velocity: vec3(),
    grounded: false,
    slideSecondsLeft: 0,
    slideCooldownSeconds: 0,
    vaulted: false,
  };
}

/** True while the player is in a slide. Read by the client for camera and animation. */
export function isSliding(state: MoveState): boolean {
  return state.slideSecondsLeft > 0;
}

/**
 * Advances one player by exactly one fixed step. Returns a new state; the input
 * state is not mutated, so callers can keep a history for reconciliation and
 * lag compensation without defensive copying.
 */
export function stepMovement(
  world: CollisionWorld,
  state: MoveState,
  input: MovementInput,
  dt: number = FIXED_DT,
): MoveState {
  const wishDir = wishDirection(input);
  const wishSpeed = desiredSpeed(input);

  let velocity = clone(state.velocity);

  // ─── Slide bookkeeping, before anything reads the slide state ──────────────
  // Timers run down every step, sliding or not, so a cooldown expires while the player is in
  // the air and a slide cannot outlive its cap by pausing.
  let slideSecondsLeft = Math.max(0, state.slideSecondsLeft - dt);
  let slideCooldownSeconds = Math.max(0, state.slideCooldownSeconds - dt);

  const holdingCrouch = (input.buttons & BUTTON.crouch) !== 0;
  const horizontalSpeed = Math.hypot(velocity.x, velocity.z);

  if (slideSecondsLeft > 0) {
    // Three ways out, and each of them is a different feel: let go of crouch (deliberate),
    // run out of speed (the slide ended itself), or leave the ground (a slide-jump, below).
    if (!holdingCrouch || horizontalSpeed < MOVEMENT.slideMinSpeed || !state.grounded) {
      slideSecondsLeft = 0;
      slideCooldownSeconds = MOVEMENT.slideCooldownSeconds;
    }
  } else if (
    state.grounded &&
    holdingCrouch &&
    (input.buttons & BUTTON.sprint) !== 0 &&
    slideCooldownSeconds <= 0 &&
    horizontalSpeed >= MOVEMENT.slideEntrySpeed
  ) {
    // Entry converts existing speed into the slide's own, in the direction already travelled
    // rather than the direction held. Steering into a slide would make it a faster walk; not
    // steering is what makes committing to one a decision.
    slideSecondsLeft = MOVEMENT.slideMaxSeconds;
    const scale = MOVEMENT.slideSpeed / Math.max(horizontalSpeed, 1e-4);
    velocity = vec3(velocity.x * scale, velocity.y, velocity.z * scale);
  }

  const sliding = slideSecondsLeft > 0;

  if (state.grounded) {
    velocity = applyFriction(velocity, dt, groundFrictionFor(velocity, wishDir, sliding));
  }

  // A slide takes no steering input at all. Accelerating during one would let a player hold
  // forward and keep the boost indefinitely, which turns a burst into a top speed.
  if (!sliding) {
    velocity = accelerate(
      velocity,
      wishDir,
      wishSpeed,
      state.grounded ? MOVEMENT.groundAccel : MOVEMENT.airAccel,
      dt,
      // Acceleration may not build past this. It never *reduces* speed, so a slide's carried
      // 12.5 survives leaving the ground and decays on its own — see `MOVEMENT.airSpeedCap`.
      MOVEMENT.airSpeedCap,
    );
  }

  let grounded = state.grounded;
  let vaulted = false;
  if (grounded && (input.buttons & BUTTON.jump) !== 0) {
    // A vault starts from the ground, never mid-air, and the reason is not stylistic: ledge
    // height is measured from the feet, so an airborne check makes any wall vaultable once
    // the player has risen far enough up it. The first version did exactly that — it refused
    // 0.9m crates (already below you by the time you were airborne) and happily vaulted 1.8m
    // cover (which measures as 1.2m from half way up). Same press, same button; the ledge
    // decides which one you get.
    const boost = tryVault(world, state.position, input.yaw);
    if (boost) {
      velocity = boost;
      vaulted = true;
    } else {
      velocity.y = MOVEMENT.jumpSpeed;
    }
    grounded = false;
    // Leaving the ground ends a slide either way, but its horizontal speed is already in
    // `velocity` and survives — that carry is the reward for setting the slide up, and it is
    // why a slide is worth learning rather than just using.
    if (sliding) {
      slideSecondsLeft = 0;
      slideCooldownSeconds = MOVEMENT.slideCooldownSeconds;
    }
  }

  // Gravity is applied every step, including while grounded; the ground probe
  // below cancels the accumulated downward velocity. Skipping it while grounded
  // would leave a player floating for one step after walking off a ledge.
  velocity.y -= MOVEMENT.gravity * dt;

  // Horizontal and vertical motion are resolved as SEPARATE sweeps, and that
  // separation is load-bearing rather than stylistic.
  //
  // A single combined sweep looks simpler and fails badly: the capsule stands only
  // COLLISION_SKIN above the floor, so any motion carrying a gravity component
  // reports the *floor* as its first contact every single tick. The floor then
  // consumes every slide iteration, walls are never tested, and the player walks
  // straight through knee-high geometry while `grounded` flickers off. Splitting
  // the passes means the horizontal sweep only ever sees walls, and the vertical
  // sweep only ever sees floors and ceilings.
  let position = state.position;

  const horizontal = moveHorizontal(
    world,
    position,
    vec3(velocity.x * dt, 0, velocity.z * dt),
    state.grounded,
  );
  position = horizontal.position;
  velocity = reprojectHorizontal(velocity, horizontal.normals);

  const vertical = moveAndSlide(world, position, vec3(0, velocity.y * dt, 0));
  position = vertical.position;
  // Any vertical contact — floor below or ceiling above — kills vertical speed.
  if (vertical.normals.length > 0) velocity.y = 0;

  const ground = probeGround(world, position, velocity);
  if (ground.grounded) {
    grounded = true;
    // Cancel only downward motion: an upward velocity here means a jump started
    // this step and must survive.
    if (velocity.y < 0) velocity.y = 0;
    position = ground.position;
  } else {
    grounded = false;
  }

  return { position, velocity, grounded, slideSecondsLeft, slideCooldownSeconds, vaulted };
}

/**
 * Velocity that carries the player over a low ledge, or null when there is nothing to vault.
 *
 * Four queries, all against the same collision world the server owns, so the answer is
 * identical on both tiers from identical inputs — which is the only property that matters
 * here. A vault the client predicts and the server refuses is a divergence with no correction
 * path, so the gate has to be exactly the same gate.
 *
 * The queries, in the order that rejects cheapest first:
 *   1. Something solid at chest height ahead?          — no obstacle, nothing to vault
 *   2. Clear air above `vaultMaxHeight` ahead?         — a wall, not a ledge
 *   3. Where is its top?                               — must be within reach
 *   4. Headroom for a body standing on it?             — otherwise you vault into a ceiling
 */
function tryVault(world: CollisionWorld, position: Vec3, yaw: number): Vec3 | null {
  const forward = vec3(-Math.sin(yaw), 0, -Math.cos(yaw));
  const feetY = position.y - MOVEMENT.height / 2;
  const probeLength = MOVEMENT.radius + MOVEMENT.vaultReach;

  // 1. A ledge has to be in front of you at roughly waist height.
  const waist = vec3(position.x, feetY + MOVEMENT.vaultMaxHeight * 0.5, position.z);
  const waistHit = world.castRay(waist, forward, probeLength);
  if (waistHit === null) return null;

  // 2. …and nothing at head height, or it is a wall and vaulting it would be flying.
  const head = vec3(position.x, feetY + MOVEMENT.vaultMaxHeight + 0.15, position.z);
  if (world.castRay(head, forward, probeLength) !== null) return null;

  // 3. Find the top by dropping onto it from above, just past the face we hit.
  const landingX = position.x + forward.x * (waistHit + MOVEMENT.radius);
  const landingZ = position.z + forward.z * (waistHit + MOVEMENT.radius);
  const dropFrom = vec3(landingX, feetY + MOVEMENT.vaultMaxHeight + 0.5, landingZ);
  const drop = world.castRay(dropFrom, vec3(0, -1, 0), MOVEMENT.vaultMaxHeight + 0.6);
  if (drop === null) return null;
  const topY = dropFrom.y - drop;
  const ledgeHeight = topY - feetY;
  // Below `stepHeight` the controller already walks up it; a vault there would be a hop for
  // no reason. Above the cap it is cover, and cover you can vault is not cover.
  if (ledgeHeight <= MOVEMENT.stepHeight || ledgeHeight > MOVEMENT.vaultMaxHeight) return null;

  // 4. Room to stand where you would land.
  const standing = vec3(landingX, topY + 0.05, landingZ);
  if (world.castRay(standing, vec3(0, 1, 0), MOVEMENT.height) !== null) return null;

  return vec3(
    forward.x * MOVEMENT.vaultForwardSpeed,
    MOVEMENT.vaultUpSpeed,
    forward.z * MOVEMENT.vaultForwardSpeed,
  );
}

/** Local movement intent rotated into world space by the player's yaw. */
export function wishDirection(input: MovementInput): Vec3 {
  let forward = 0;
  let strafe = 0;
  if (input.buttons & BUTTON.forward) forward += 1;
  if (input.buttons & BUTTON.back) forward -= 1;
  if (input.buttons & BUTTON.right) strafe += 1;
  if (input.buttons & BUTTON.left) strafe -= 1;

  if (forward === 0 && strafe === 0) return vec3();

  // Right-handed, Y-up, matching three.js: at yaw 0 the player faces -Z and its
  // right hand points +X. Increasing yaw rotates counter-clockwise seen from
  // above, so yaw = π/2 faces -X.
  //
  //   forward = (-sin yaw, 0, -cos yaw)
  //   right   = ( cos yaw, 0, -sin yaw)
  //
  // The Z terms are negative. Getting that sign wrong is invisible in isolation —
  // movement still feels fine, it is just mirrored front-to-back — and only shows
  // up as the player walking away from geometry it was aimed at.
  const sin = Math.sin(input.yaw);
  const cos = Math.cos(input.yaw);
  const x = strafe * cos - forward * sin;
  const z = -(strafe * sin + forward * cos);

  return normalize(vec3(x, 0, z));
}

export function desiredSpeed(input: MovementInput): number {
  const base =
    input.buttons & BUTTON.crouch
      ? MOVEMENT.crouchSpeed
      : input.buttons & BUTTON.sprint
        ? MOVEMENT.sprintSpeed
        : MOVEMENT.walkSpeed;

  // The weapon scales the stance rather than replacing it, so a sniper is slower than an AR
  // at every stance rather than having its own speed table.
  return base * (input.speedMultiplier ?? 1);
}

/**
 * Quake-style friction: proportional to current speed, applied to the horizontal
 * component only so it never fights gravity.
 */
/**
 * True when the player is pressing *against* the way they are travelling.
 *
 * The dot product is taken against the normalised horizontal velocity, so this asks about
 * direction only and does not care how fast they are going. −0.5 is a 120° disagreement:
 * clearly opposing, not merely turning. Strafing across your own momentum — which is how you
 * corner, and which must stay cheap — sits near zero and is unaffected.
 */
function isCounterStrafing(velocity: Vec3, wishDir: Vec3): boolean {
  const speed = Math.hypot(velocity.x, velocity.z);
  // Below walking pace there is nothing left to cancel, and normalising a near-zero vector
  // amplifies float noise into a direction it does not have.
  if (speed < MOVEMENT.crouchSpeed) return false;
  if (wishDir.x === 0 && wishDir.z === 0) return false;
  return (velocity.x * wishDir.x + velocity.z * wishDir.z) / speed < -0.5;
}

/**
 * Which friction coefficient applies this tick.
 *
 * A slide is checked first and wins outright: a slide takes no steering input at all, so
 * "pressing against your velocity" is not a thing that can happen inside one, and letting
 * counter-strafe friction apply there would let a player cancel their own slide with a key
 * the slide is supposed to ignore.
 */
function groundFrictionFor(velocity: Vec3, wishDir: Vec3, sliding: boolean): number {
  if (sliding) return MOVEMENT.slideFriction;
  if (isCounterStrafing(velocity, wishDir)) return MOVEMENT.counterStrafeFriction;
  return MOVEMENT.friction;
}

function applyFriction(velocity: Vec3, dt: number, coefficient: number = MOVEMENT.friction): Vec3 {
  const horizontal = vec3(velocity.x, 0, velocity.z);
  const speed = length(horizontal);
  if (speed < 1e-4) return vec3(0, velocity.y, 0);

  const drop = speed * coefficient * dt;
  const factor = Math.max(0, speed - drop) / speed;
  return vec3(velocity.x * factor, velocity.y, velocity.z * factor);
}

/**
 * Quake-style acceleration: only ever adds the shortfall between current speed
 * *along the wish direction* and the desired speed. Speed already carried in
 * another direction is untouched, which is what makes air control feel the way
 * players expect from this genre.
 */
/**
 * Scales horizontal velocity down to `cap`, leaving the vertical component alone.
 *
 * Only ever reduces. A player already below the cap, and every falling player, comes through
 * untouched — so this cannot quietly become a floor on speed, and it cannot interfere with
 * gravity.
 */
export function clampHorizontalSpeed(velocity: Vec3, cap: number): Vec3 {
  const speed = Math.hypot(velocity.x, velocity.z);
  if (speed <= cap) return velocity;
  const scale = cap / speed;
  return vec3(velocity.x * scale, velocity.y, velocity.z * scale);
}

function accelerate(
  velocity: Vec3,
  wishDir: Vec3,
  wishSpeed: number,
  accel: number,
  dt: number,
  speedCap = Infinity,
): Vec3 {
  if (wishSpeed <= 0) return velocity;

  const current = dot(velocity, wishDir);
  const shortfall = wishSpeed - current;
  if (shortfall <= 0) return velocity;

  const step = Math.min(accel * wishSpeed * dt, shortfall);
  const accelerated = addScaled(velocity, wishDir, step);

  /*
   * The cap bounds what acceleration may *build*, never what a player may *have*.
   *
   * `Math.max(speedCap, before)` is the whole idea: someone already travelling faster than
   * the cap — out of a slide, off a vault — keeps every bit of it, and simply gains nothing
   * more from steering in the air. Friction and gravity take it back, as they always did.
   *
   * The first version clamped total speed after this function returned, which read as the
   * same rule and was not: it deleted the slide's carried momentum, quietly removing the
   * reward for setting a slide up. A bound that fires in the case you were designing *for*
   * is the wrong bound.
   */
  const before = Math.hypot(velocity.x, velocity.z);
  return clampHorizontalSpeed(accelerated, Math.max(speedCap, before));
}

export interface SlideResult {
  position: Vec3;
  /** Normals of every surface hit this move, for velocity reprojection. */
  normals: Vec3[];
}

/**
 * Collide-and-slide: move, and on contact remove the velocity component into the
 * surface and continue with what remains. Iterating handles a floor plus two
 * walls (a corner); beyond that the player is wedged and stops.
 */
export function moveAndSlide(world: CollisionWorld, origin: Vec3, motion: Vec3): SlideResult {
  let position = clone(origin);
  let remaining = clone(motion);
  const normals: Vec3[] = [];

  for (let i = 0; i < MOVEMENT.slideIterations; i++) {
    if (length(remaining) < 1e-7) break;

    const hit = world.sweepCapsule(position, remaining, MOVEMENT.radius, CAPSULE_HALF_HEIGHT);

    if (!hit) {
      position = addScaled(position, remaining, 1);
      remaining = vec3();
      break;
    }

    // Advance to the contact, then push out along the surface normal so the
    // capsule ends the step a hair clear of the geometry rather than exactly
    // touching it. Without this standoff the next sweep starts in contact and
    // returns fraction 0, and the player sticks to whatever it brushed against.
    position = addScaled(position, remaining, hit.fraction);
    position = addScaled(position, hit.normal, COLLISION_SKIN);
    remaining = scale(remaining, 1 - hit.fraction);
    normals.push(hit.normal);

    remaining = projectOnPlane(remaining, hit.normal);
  }

  return { position, normals };
}

/** Horizontal distance between two points, ignoring height. */
function horizontalDistance(a: Vec3, b: Vec3): number {
  return length(vec3(b.x - a.x, 0, b.z - a.z));
}

/** How far `from → to` advanced along the horizontal direction of `motion`. Sideways slide
 * along a wall counts for nothing; being pushed back counts negative. */
function progressAlong(from: Vec3, to: Vec3, motion: Vec3): number {
  const flat = length(vec3(motion.x, 0, motion.z));
  if (flat < 1e-9) return horizontalDistance(from, to);
  return ((to.x - from.x) * motion.x + (to.z - from.z) * motion.z) / flat;
}

/**
 * Horizontal movement, with step-up.
 *
 * Structured as "retry the whole move from a raised position, then snap back
 * down" rather than as a step attempt inside the slide loop. The in-loop version
 * is tempting and subtly wrong: it re-enters on every slide iteration and every
 * tick, so a player pressed against a step climbs its face a few centimetres per
 * tick — ratcheting upward while making no forward progress, instead of stepping
 * over it once.
 *
 * Only attempted while grounded, which is what prevents players from air-stepping
 * their way up a wall mid-jump.
 */
function moveHorizontal(
  world: CollisionWorld,
  position: Vec3,
  motion: Vec3,
  grounded: boolean,
): SlideResult {
  const flat = moveAndSlide(world, position, motion);
  if (!grounded) return flat;

  const wanted = length(motion);
  if (wanted < 1e-7) return flat;

  // Progress is measured ALONG the intended motion, not as distance travelled: a capsule that
  // meets a riser at an angle slides sideways along its face, and that sideways slide counted
  // as progress made the step attempt "lose" the comparison below — the player ran along the
  // bottom step instead of up it, from every oblique approach (measured 2026-09-08: 25° and
  // 45° approaches failed 3 of 4 stair geometries with the distance metric, 0 with this one).
  const flatProgress = progressAlong(position, flat.position, motion);
  // Untouched: there is nothing to step over. Note "untouched", not "made most of its
  // progress": the capsule's rounded bottom meeting a riser's EDGE slides UP the edge with
  // nearly full horizontal progress, so a progress gate (this used to be 95%) never attempted
  // the step, the player rode the edge to a slanted contact `probeGround` refused, lost
  // `grounded`, and with it the right to step at all — stalled half way up a stair, from some
  // approach phases and not others. Measured 2026-09-08: 0.3m risers cleared 20 of 30 runs.
  // Any contact during the flat move now earns a step attempt; the two results are compared
  // on progress below, so an open floor still costs one sweep and a real wall still wins.
  if (flat.normals.length === 0) return flat;

  // The lift itself needs headroom, otherwise this is a crawl space, not a step.
  const lift = vec3(0, MOVEMENT.stepHeight, 0);
  if (world.sweepCapsule(position, lift, MOVEMENT.radius, CAPSULE_HALF_HEIGHT)) return flat;

  const lifted = addScaled(position, lift, 1);
  const stepped = moveAndSlide(world, lifted, motion);

  // The lift has to actually buy forward progress; if it does not, this is a real
  // wall and the player slides along it.
  if (progressAlong(lifted, stepped.position, motion) <= flatProgress + 1e-4) return flat;

  // Settle back down onto whatever is under the new position. Any contact within the drop is
  // accepted — deliberately NOT gated on the contact normal being walkable. The first contact
  // is usually the step's rounded EDGE, whose capsule-vs-box normal is slanted even though the
  // surface itself is flat, and how far onto the step one tick of motion travels decides how
  // much edge versus top the settle touches: at 7 m/s the normal happened to read walkable, at
  // 4.6 m/s the same kerb read as a 52° slope and the player was pinned against knee-high
  // geometry forever (found the day the pace was retuned — the old speeds passed the old
  // normal gate by 0.018). Riding the edge for a few ticks converges onto the top; a genuinely
  // steep surface stepped onto here is refused by `probeGround` on the very next tick and the
  // player slides off — a contained failure, unlike a player glued to a kerb.
  const drop = vec3(0, -(MOVEMENT.stepHeight + COLLISION_SKIN), 0);
  const settle = world.sweepCapsule(stepped.position, drop, MOVEMENT.radius, CAPSULE_HALF_HEIGHT);
  // Nothing underneath at all would leave the player hovering in mid-air.
  if (!settle) return flat;

  const contact = addScaled(stepped.position, drop, settle.fraction);
  const landed = addScaled(contact, settle.normal, COLLISION_SKIN);
  // Stepping must not lower the player: a settle below where the flat move ended is a step
  // attempt over open ground, and the flat result already stands on it.
  if (landed.y < flat.position.y - 1e-4) return flat;
  return { position: landed, normals: stepped.normals };
}

/**
 * Removes velocity into every wall hit during the horizontal pass, so a player
 * sliding along a wall stops accelerating into it.
 *
 * Each contact normal is flattened to horizontal before projecting, and that is
 * not a detail: a contact against the *edge* of a step reports a normal that is
 * mostly +Y, and projecting full 3D velocity onto it converts forward speed into
 * upward speed. The player gets flung into the air on contact with knee-high
 * geometry — a launch of ~1.7 m/s in testing, which reads as the level randomly
 * bouncing you. Flattening keeps a horizontal pass strictly horizontal; vertical
 * speed is owned by the vertical pass alone.
 */
function reprojectHorizontal(velocity: Vec3, normals: Vec3[]): Vec3 {
  let out = velocity;
  for (const normal of normals) {
    // Measure the horizontal part BEFORE normalising it. The check below used to run on the
    // already-normalised vector, whose length is 1 for every normal that has any horizontal
    // component at all — so "a near-vertical normal has nothing horizontal to block" never
    // rejected anything, and a floor contact reported as (0.001, 0.999, 0) was treated as a
    // wall standing dead ahead: the player's whole X velocity was deleted for that tick.
    //
    // Measured on an empty floor, walking forward with nothing else held: one such tick every
    // few seconds at some yaws, each costing a fraction of a second of one axis, which
    // integrates into a visible curve — 0.21 m off a straight 12.6 m walk, always toward the
    // same side for a given heading ("персонажа утягивает вправо при движении прямо",
    // 2026-09-18). Away from those yaws the same walk lands within 0.6 mm.
    const flatLength = Math.hypot(normal.x, normal.z);
    // Walkable ground is not a wall. `minGroundNormalY` is the same threshold `probeGround`
    // uses to decide what the player can stand on, so the two passes agree about what a floor
    // is; anything flatter than that is a slope or a wall and does block horizontal motion.
    if (flatLength < 1e-6 || normal.y >= MOVEMENT.minGroundNormalY) continue;
    const flat = normalize(vec3(normal.x, 0, normal.z));
    if (dot(out, flat) < 0) out = projectOnPlane(out, flat);
  }
  return out;
}

export interface GroundProbe {
  grounded: boolean;
  position: Vec3;
}

/**
 * Looks just below the feet for walkable ground, and snaps down onto it.
 *
 * Snapping matters more than it looks: without it, a player walking down a gentle
 * slope or over a step leaves the ground for a frame every time, which disables
 * ground friction and jumping intermittently — it reads as the controller being
 * "floaty" or "sticky" and is very hard to diagnose from the symptom.
 */
export function probeGround(world: CollisionWorld, position: Vec3, velocity: Vec3): GroundProbe {
  if (velocity.y > MOVEMENT.maxSnapUpSpeed) {
    return { grounded: false, position };
  }

  const probe = vec3(0, -MOVEMENT.groundProbe, 0);
  const hit = world.sweepCapsule(position, probe, MOVEMENT.radius, CAPSULE_HALF_HEIGHT);

  if (!hit || hit.normal.y < MOVEMENT.minGroundNormalY) {
    // Two cases the sweep alone gets wrong, both on stairs and kerbs, both fixed the same way.
    // (1) The capsule's bottom hemisphere rests on an EDGE — a riser's lip — whose
    // capsule-vs-box normal is slanted although the surface under the player is flat.
    // (2) The capsule already touches that edge within the collision skin, and a shape cast
    // that begins in contact reports nothing at all. Either way the controller concluded
    // "airborne", stopped stepping (`moveHorizontal` steps only while grounded), let gravity
    // build, and the player fell a tick and stepped a tick — the stair "teleporting" report
    // (2026-09-08). Rays answer the question the sweep cannot: solid within standing reach
    // below means standing. The position is left where it is — snapping onto a slanted
    // contact is the ratchet the step logic exists to avoid — and the next tick's step-up
    // resolves it.
    const reach = CAPSULE_HALF_HEIGHT + MOVEMENT.radius + MOVEMENT.groundProbe + COLLISION_SKIN;
    const down = vec3(0, -1, 0);
    if (world.castRay(position, down, reach) !== null) return { grounded: true, position };

    // The centre ray alone still missed 18 of 30 approach phases on a 0.3m riser (measured
    // 2026-09-08): while the capsule rides the riser's lip, its centre is still BESIDE the
    // step, so a ray from the centre falls past the step to the floor below — out of reach —
    // and the player hovered, ungrounded and unable to step, perched on the edge; one phase
    // stalled there for eight ticks. Every brush is an axis-aligned box, so a slanted contact
    // from below is always a box EDGE with a flat top next to it, never a slope. Aim the ray at
    // that brush: one radius toward it from the centre lands past its edge, onto its top face.
    // A walkable top within reach means the step logic will carry the player over it — or,
    // from the other side, that the player is standing on a ledge with their centre past its
    // edge, which a shooter should allow (an FPS is a box on a ledge, not a ball). The hit
    // must be a real top face, not the side of a brush the ray started inside of, so a
    // zero-distance hit or a non-walkable normal is rejected; and a contact with no upward
    // component is a wall face the capsule is pressed against while falling, not an edge.
    if (hit && hit.normal.y > 0.05) {
      const away = normalize(vec3(hit.normal.x, 0, hit.normal.z));
      if (length(away) > 1e-6) {
        const origin = addScaled(position, away, -MOVEMENT.radius);
        const top = world.castRayWithNormal(origin, down, reach);
        if (top && top.distance > 1e-3 && top.normal.y >= MOVEMENT.minGroundNormalY) {
          return { grounded: true, position };
        }
      }
    }
    return { grounded: false, position };
  }

  // Settle onto the surface but keep the standoff gap, matching moveAndSlide.
  const contact = addScaled(position, probe, hit.fraction);
  return { grounded: true, position: addScaled(contact, hit.normal, COLLISION_SKIN) };
}

/** Eye position for rendering and for the server's hitscan origin. */
export function eyePosition(state: MoveState): Vec3 {
  return vec3(
    state.position.x,
    state.position.y - MOVEMENT.height / 2 + MOVEMENT.eyeHeight,
    state.position.z,
  );
}
